import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { bindActionCreators } from 'redux'
import TextField from 'components/common/textfield'
import Icon from 'components/common/icon'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import searchModalDuck from 'components/search/search-section/duck'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import {
  closeSearchPanel,
  openSearchResults,
  storeTheSearchWord,
} from '../utils'
import styles from './style.scss'
import searchCopy from '../copy'

export const HEADER_SEARCH_FORM_ID = 'header-search-form'

export const SearchForm = ({
  alwaysShowClose,
  className,
  closeSearchModal,
  i18n,
  onFocus,
  openResults,
  placeholder,
  searchQuery,
  searchWord,
  setSearchWord,
  t,
}) => {
  const searchBoxPlaceholder =
    placeholder || searchCopy(t, i18n?.language, 'placeholder')
  const inputRef = useRef(null)
  const bindInput = useCallback((input) => {
    inputRef.current = input
  }, [])
  const clearSearch = () => {
    setSearchWord('')
    inputRef.current?.focus()
  }

  const search = (e) => {
    e?.preventDefault()

    // Do searching for a non-empty field.
    if (searchWord) {
      storeTheSearchWord(searchWord, setSearchWord)
      openSearchResults(searchWord, openResults)
    }
  }

  // ASOS: Esc closes search
  const closeOnEscape = (e) => {
    if (e.key === 'Escape') {
      inputRef.current?.blur()
      closeSearchPanel({ closeSearchModal, query: searchQuery })
    }
  }

  return (
    <form
      className={clsx('search-form-container flex-form', className)}
      id={HEADER_SEARCH_FORM_ID}
      onSubmit={search}
    >
      <style jsx>{styles}</style>
      <TextField
        fullWidth
        inputRef={bindInput}
        onChange={(e) => e && setSearchWord(e.target.value)}
        value={searchWord}
        onFocus={onFocus}
        inputProps={{ onKeyDown: closeOnEscape }}
        placeholder={searchBoxPlaceholder}
        prefix={<Icon src={ICONS.search} size={ICON_SIZE.medium} />}
        postfix={
          searchWord ? (
            <button
              type="button"
              className="search-clear"
              aria-label={searchCopy(t, i18n?.language, 'clear')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearSearch}
            >
              <Icon src={ICONS.close} size={ICON_SIZE.small} />
            </button>
          ) : undefined
        }
        typo={Typo.body2}
      />
      {alwaysShowClose && (
        <button
          type="button"
          className="search-close"
          onClick={() =>
            closeSearchPanel({ closeSearchModal, query: searchQuery })
          }
          aria-label={searchCopy(t, i18n?.language, 'close')}
        >
          {searchCopy(t, i18n?.language, 'closeLabel')}
        </button>
      )}
    </form>
  )
}

SearchForm.propTypes = {
  alwaysShowClose: PropTypes.bool,
  className: PropTypes.string,
  closeSearchModal: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ language: PropTypes.string }),
  onFocus: PropTypes.func,
  openResults: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  searchQuery: PropTypes.string,
  searchWord: PropTypes.string,
  setSearchWord: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}

SearchForm.defaultProps = {
  alwaysShowClose: false,
  className: '',
  i18n: undefined,
  onFocus: undefined,
  placeholder: '',
  searchQuery: '',
  searchWord: '',
}

export default compose(
  connect(
    (state) => ({ searchQuery: state.searchModal.query }),
    (dispatch) =>
      bindActionCreators(
        {
          closeSearchModal: searchModalDuck.creators.closeSearchModal,
          openResults: searchModalDuck.creators.openSearchResults,
        },
        dispatch,
      ),
  ),
  withI18next(),
)(SearchForm)
